import React, { useContext, useEffect, useState } from "react";
import moment from "moment";
import { IToastContext, ToastContext } from "../../../providers/Toast";
import { getUserAppointments, sendAppointmentResponse } from "../../../context/appointments/action";
import Button from "../../../components/shared/button/Button";
import { DB_DEFAULTS } from "../../../firebase/collections";
import { useTranslation } from "react-i18next";
import AddToCalendar from "react-add-to-calendar";
import { Appointment, UserDetail } from "../../../data/interfaces";
import { updateMembersAvailability } from "../../../context/stands/action";
import Tooltip from "../../../components/shared/tooltip/Tooltip";
import { IModalContext, ModalContext } from "../../../providers/Modal";
import { useHistory } from "react-router-dom";
import { composePathWithParams } from "../../../utils/url";
import { ROUTES } from "../../../routing/routes";

// To use jQuery
declare const $: any;

// -----------------------------------------------------------------
// I n t e r f a c e s
// -----------------------------------------------------------------
interface Props {
  uid?: string;
  onLoad: any;
  userData: UserDetail;
  loadedData?: any[] | null;
}

const MyAppointmentsList = ({ uid, onLoad, userData, loadedData, ...restProps }: Props) => {
  // -----------------------------------------------------------------
  // L o c a l   v a r s
  // -----------------------------------------------------------------
  const history = useHistory();

  // Access to toast manager hook that you may use to show toasts.
  const { openToast } = useContext<IToastContext>(ToastContext);

  // Access to modal manager hook that you may use to open and close modals.
  const { openModal } = useContext<IModalContext>(ModalContext);

  // Access to i18n instance that you may use to translate your content.
  const { t } = useTranslation();

  // -----------------------------------------------------------------
  // N a v i g a t i o n   v a r s
  // -----------------------------------------------------------------

  // -----------------------------------------------------------------
  // S t a t e
  // -----------------------------------------------------------------
  // Loading list
  const [loading, setLoading] = useState<boolean>(false);

  // Eventual error
  const [errors, setErrors] = useState<any>(false);

  // Local array of data
  const [data, setData] = useState<Appointment[]>([]);

  // Pagination
  const [lastItem, setLastItem] = useState<Appointment | null>(null);
  const [limit, setLimit] = useState<number>(DB_DEFAULTS.LIMIT);
  const [canLoadMore, setCanLoadMore] = useState<boolean>(true);

  // Response loading (accept, decline appointment)
  const [appointmentResponseLoading, setAppointmentResponseLoading] = useState<boolean>(false);

  // -----------------------------------------------------------------
  // R e f s  (DOM)
  // -----------------------------------------------------------------

  // -----------------------------------------------------------------
  // u s e S e l e c t o r   m e t h o d s  (redux)
  // -----------------------------------------------------------------

  // -----------------------------------------------------------------
  // W o r k i n g   m e t h o d s
  // -----------------------------------------------------------------
  const goToStand = (stand: any): void => {
    history.push(composePathWithParams(ROUTES.STAND_DETAIL, [{ id: stand.id }]));
  };

  const handleLoadingVars = (array: any[]): void => {
    if (!array || array.length < limit) {
      setCanLoadMore(false);
    }

    // Track last element value for pagination
    setLastItem(array[array.length - 1]);
  };

  /**
   * Handle user's response to an appointment (accept , decline)
   * @function
   * @async
   *
   * @param appointment
   * @param accepted
   */
  const handleAppointmentResponse = async (appointment: Appointment, accepted: boolean) => {
    if (!appointment) {
      return;
    }

    setAppointmentResponseLoading(true);

    try {
      await sendAppointmentResponse(appointment, accepted);
      if (accepted) {
        await updateMembersAvailability(
          appointment.stand.id,
          appointment.members,
          appointment.start_datetime
        );
      }
      await loadData(true);

      openToast({
        type: "success",
        title: "success.general.title",
        message: accepted ? "success.appointment.accepted" : "success.appointment.declined",
        callback: () => {},
      });
    } catch (err) {
      console.error(err);
      openToast({
        type: "error",
        title: "errors.general.title",
        message: "errors.general.description",
        callback: () => {},
      });
    } finally {
      setAppointmentResponseLoading(false);
    }
  };

  /**
   * Load events from database
   * @function
   * @async
   */
  const loadData = async (reset = false) => {
    if (!uid) {
      return;
    }

    setLoading(true);

    try {
      const _data = await getUserAppointments(uid, limit, !reset ? lastItem : undefined);
      handleLoadingVars(_data);

      setData(reset ? _data : [...data, ..._data]);
    } catch (err) {
      setErrors(err);
      console.error(err);
      openToast({
        type: "error",
        title: "errors.general.title",
        message: "errors.general.description",
        callback: () => {},
      });
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------------------------------------------
  // R e n d e r   m e t h o d s
  // -----------------------------------------------------------------
  const renderType = (doc: any): React.ReactNode => {
    return (
      <div className="value">
        {(() => {
          switch (doc.type) {
            case "phone":
              return (
                <>
                  <i className="fas fa-phone"></i>
                  <span>Phone call</span>
                </>
              );
            case "video": {
              return (
                <>
                  <i className="fas fa-video"></i>
                  <span>Video call</span>
                </>
              );
            }
            default:
              return null;
          }
        })()}
      </div>
    );
  };

  /**
   * Add to calendar button
   * @function
   *
   * @param appointment
   */
  const renderAddToCalendar = (appointment: Appointment) => {
    // If expired or not already accepted/declined
    if (moment(appointment.start_datetime).isSameOrBefore(moment()) || !appointment.accepted) {
      return;
    }

    const addToCalendarOptions = {
      config: {
        title: t("pages.appointments.addToCalendar.title", {
          stand: appointment.stand.name,
        }),
        description: t("pages.appointments.addToCalendar.description", {
          partecipants: appointment.members
            .map((p: any) => {
              return p.firstname + " " + p.lastname;
            })
            .join(", "),
        }),
        startTime: moment(appointment.start_datetime).toISOString(true),
        endTime: moment(appointment.end_datetime).toISOString(true),
        location: appointment.jitsi_url,
      },
      icon: { " far fa-calendar-plus": "left" },
    };

    return (
      <>
        <AddToCalendar
          event={addToCalendarOptions.config}
          buttonTemplate={addToCalendarOptions.icon}
          buttonWrapperClass={"btn btn-default btn-circle"}
          displayItemIcons={false}
          buttonLabel=""
        />
      </>
    );
  };

  // -----------------------------------------------------------------
  // L i f e c y c l e
  // -----------------------------------------------------------------
  /**
   * This method is called the first time the component is mounted
   * @function
   */
  const init = (): void => {
    console.log("[MyAppointmentsList] init");

    // If loaded, set from parent. Else load from db
    if (!!loadedData) {
      console.log("Preloaded data, no query");
      setData(loadedData);
      handleLoadingVars(loadedData);
    } else {
      console.log("No preloaded data, do query");
      loadData();
    }
  };

  /**
   * This method is called when the component is unmounted
   * @function
   */
  const destroy = (): void => {
    console.log("[MyAppointmentsList] destroy");
    // destroy all
  };

  // -----------------------------------------------------------------
  // u s e E f f e c t   m e t h o d s
  // -----------------------------------------------------------------
  /**
   * This hook is called once when the component is mounted
   */
  useEffect(() => {
    init();
    return () => {
      destroy();
    };
  }, []);

  /**
   * Hook to data in order to load data
   */
  useEffect(() => {
    if (data && data.length) {
      // Emit load
      onLoad(data);
    }
  }, [data]);

  // -----------------------------------------------------------------
  // T e m p l a t e
  // -----------------------------------------------------------------
  return (
    <div className="my-appointments profile-section">
      <div className="header">
        <h1>{t("pages.profile.my_appointments.title")}</h1>
        <p>{t("pages.profile.my_contacts.description")}</p>
        <div className="image">
          <img src="/assets/images/undraw/appointments.png" />
        </div>
      </div>

      {!!data &&
        data.map((doc: any) => (
          <div
            className={`my-appointment-box${doc.accepted === false ? " disabled" : ""}`}
            key={doc.id}
            id={doc.id}
            // onClick={() => toggleBox(doc.id)}
          >
            <div
              className="image"
              style={{ backgroundImage: `url(${doc.stand?.logo_image_url})` }}
            ></div>
            <div className="info">
              <div className="value link" onClick={() => goToStand(doc.stand)}>
                <i className="fas fa-thumbtack"></i>
                <span>{doc.stand?.name}</span>
              </div>
              {renderType(doc)}
              <div className="value">
                <i className={"far fa-calendar"} />
                <span>{moment(doc.start_datetime).format("dddd, MMMM Do YYYY")}</span>
              </div>
              <div className="value">
                <i className={"far fa-clock"} />
                <span>
                  {moment(doc.start_datetime).format("HH:mm")}-
                  {moment(doc.end_datetime).format("HH:mm")}
                </span>
              </div>
              <div className="value">
                <i className={"fas fa-users"} />
                <span>
                  {doc.visitor.firstname} {doc.visitor.lastname},&nbsp;
                  {doc.members
                    .map((p: any) => {
                      return p.firstname + " " + p.lastname;
                    })
                    .join(", ")}
                </span>
              </div>
            </div>

            <div className="ctas">
              {/* Check who created the appointment */}
              {doc.created_by === userData.uid ? (
                <>
                  {doc.created_by === userData.uid && typeof doc.accepted === "undefined" && (
                    <p className={"pending"}>{t("pages.appointments.ctas.pending")}</p>
                  )}
                </>
              ) : (
                <>
                  {userData.international_buyer && typeof doc.accepted === "undefined" && (
                    <>
                      <Button
                        className="btn btn-danger btn-sm"
                        disabled={appointmentResponseLoading}
                        onClick={() => {
                          openModal({
                            title: t("pages.profile.my_appointments.decline.title"),
                            message: t("pages.profile.my_appointments.decline.message"),
                            callback: () => () => handleAppointmentResponse(doc, false),
                          });
                        }}
                      >
                        {t("pages.appointments.ctas.reject")}
                      </Button>
                      <Button
                        className="btn btn-primary btn-sm"
                        disabled={appointmentResponseLoading}
                        onClick={() => {
                          openModal({
                            title: t("pages.profile.my_appointments.accept.title"),
                            message: t("pages.profile.my_appointments.accept.message"),
                            callback: () => () => handleAppointmentResponse(doc, true),
                          });
                        }}
                      >
                        {t("pages.appointments.ctas.confirm")}
                      </Button>
                    </>
                  )}
                </>
              )}

              {/* Add to calendar button */}
              {renderAddToCalendar(doc)}

              {/* If accepted, show jisti url */}
              {doc.accepted && (
                <>
                  {/* TODO: RENDERIZZARE ALLO STESSO MODO IL LINK AL CANALE MATTERMOST DELL'APPUNTAMENTO
                                    <a href={doc.mattermost_url}
                                       target={"_blank"}
                                       className="btn btn-default btn-circle" type="button">
                                        <i className="fas fa-comment-dots" />
                                    </a>*/}
                  <a
                    href={doc.jitsi_url}
                    title={t("pages.appointments.ctas.goToJitsi")}
                    target={"_blank"}
                    className="btn btn-default btn-circle"
                    type="button"
                    data-tip={t("tooltips.appointments.startMeeting")}
                  >
                    <i className="fas fa-video" />
                  </a>

                  <Tooltip />
                </>
              )}

              {/* If rejected, show jisti url */}
              {doc.accepted === false && <p>{t("pages.appointments.ctas.rejected")}</p>}
            </div>
          </div>
        ))}

      {/* Loading */}
      {!!loading && (
        <div className="list-loading">
          <i className="fas fa-spin fa-spinner"></i>
        </div>
      )}

      {/* Load more button */}
      {!loading && !!canLoadMore && (
        <div className="load-more">
          <Button className="btn btn-primary" onClick={loadData}>
            Load more
          </Button>
        </div>
      )}
    </div>
  );
};
export default MyAppointmentsList;
